import { Users, Target, Package, MapPin, Workflow, BarChart3, Zap, Handshake, Presentation } from "lucide-react";

interface ConsultingApproachProps {
  language: 'en' | 'de';
}

export const ConsultingApproach = ({ language }: ConsultingApproachProps) => {
  const content = {
    en: {
      title: "Our consulting approach",
      subtitle: "Strategic frameworks that drive organizational excellence",
      approaches: [
        {
          icon: Users,
          title: "People",
          description: "We design leadership architectures that align talent, succession planning, and capability-building initiatives. By combining evidence-based assessment tools with individualized coaching, we ensure the right executives occupy pivotal roles, cultivate adaptive mindsets, and sustain learning loops that translate organizational ambition into measurable growth trajectories."
        },
        {
          icon: Target,
          title: "Purpose",
          description: "We facilitate mission clarification through structured inquiry, stakeholder dialogue, and narrative synthesis. The outcome is a shared strategic compass that articulates meaning, guides capital allocation, and embeds ethical intent, enabling teams to prioritize initiatives coherently and mobilize engagement around a clearly expressed raison d'être."
        },
        {
          icon: Package,
          title: "Product",
          description: "We interrogate customer journeys, value propositions, and monetization logic to reposition offerings around demonstrable client outcomes. Using qualitative insight and market analytics, we refine product portfolios so executive decisions emphasize relevance, differentiation, and measurable value creation for every priority segment."
        },
        {
          icon: MapPin,
          title: "Place",
          description: "We calibrate market timing, channel orchestration, and service availability to match stakeholder expectations. By integrating capacity modeling with go-to-market analytics, we optimize the contexts in which solutions are accessed, ensuring distribution agility and responsive presence across physical, digital, and hybrid interfaces."
        },
        {
          icon: Workflow,
          title: "Process",
          description: "We map, evaluate, and redesign operational rules, structures, and governance practices. Through applied systems thinking and continuous improvement methodology, we institutionalize processes that remain fit for purpose, embedding adaptive mechanisms that allow strategic initiatives to scale without sacrificing compliance or quality."
        },
        {
          icon: BarChart3,
          title: "Performance",
          description: "We construct evidence-based scorecards that define impact in quantitative and qualitative terms. Linking metrics to strategic objectives and data infrastructure, we equip leadership with transparent insights, enabling fact-driven accountability, predictive steering, and disciplined communication of results to boards and stakeholders."
        },
        {
          icon: Zap,
          title: "Power",
          description: "We architect decision-rights frameworks, escalation paths, and empowerment protocols that accelerate executive responsiveness. By clarifying authority boundaries and enabling distributed judgment, we expand the organization's capacity to act decisively under uncertainty while maintaining alignment with enterprise strategy."
        },
        {
          icon: Handshake,
          title: "Partnership",
          description: "We cultivate ecosystems of allies, co-innovation partners, and institutional sponsors to advance shared goals. Leveraging strategic fit analyses and governance blueprints, we structure collaborative arrangements that maximize complementarities, protect mutual interests, and translate joint initiatives into sustainable, co-created value."
        },
        {
          icon: Presentation,
          title: "Presentation",
          description: "Effective presentation demands rhetorical clarity and visual salience while sustaining disciplined inquiry into unseen dynamics—implicit assumptions, affective undercurrents, latent power asymmetries. By articulating arguments that resonate publicly and interrogating what remains unspoken, leaders cultivate credibility, anticipate resistance, and transform audience understanding beyond the immediate spectacle."
        }
      ]
    },
    de: {
      title: "Unser Beratungsansatz",
      subtitle: "Strategische Rahmenbedingungen für organisatorische Exzellenz",
      approaches: [
        {
          icon: Users,
          title: "Menschen",
          description: "Wir entwickeln Führungsarchitekturen, die Talent, Nachfolgeplanung und Kompetenzaufbau-Initiativen aufeinander abstimmen. Durch die Kombination evidenzbasierter Bewertungsinstrumente mit individualisiertem Coaching stellen wir sicher, dass die richtigen Führungskräfte zentrale Positionen besetzen, adaptive Denkweisen kultivieren und Lernschleifen aufrechterhalten, die organisatorischen Ehrgeiz in messbare Wachstumspfade übersetzen."
        },
        {
          icon: Target,
          title: "Zweck",
          description: "Wir ermöglichen Missionsklärung durch strukturierte Befragung, Stakeholder-Dialog und narrative Synthese. Das Ergebnis ist ein gemeinsamer strategischer Kompass, der Bedeutung artikuliert, Kapitalallokation leitet und ethische Absicht verankert, wodurch Teams Initiativen kohärent priorisieren und Engagement um eine klar formulierte Daseinsberechtigung mobilisieren können."
        },
        {
          icon: Package,
          title: "Produkt",
          description: "Wir hinterfragen Customer Journeys, Wertversprechen und Monetarisierungslogik, um Angebote auf nachweisbare Kundenergebnisse auszurichten. Unter Verwendung qualitativer Erkenntnisse und Marktanalysen verfeinern wir Produktportfolios, sodass Führungsentscheidungen Relevanz, Differenzierung und messbare Wertschöpfung für jedes Prioritätssegment betonen."
        },
        {
          icon: MapPin,
          title: "Ort",
          description: "Wir kalibrieren Markt-Timing, Kanalorchestration und Service-Verfügbarkeit, um Stakeholder-Erwartungen zu erfüllen. Durch Integration von Kapazitätsmodellierung mit Go-to-Market-Analytik optimieren wir die Kontexte, in denen Lösungen zugänglich sind, und gewährleisten Vertriebsagilität und responsive Präsenz über physische, digitale und hybride Schnittstellen hinweg."
        },
        {
          icon: Workflow,
          title: "Prozess",
          description: "Wir kartieren, evaluieren und gestalten operative Regeln, Strukturen und Governance-Praktiken neu. Durch angewandtes Systemdenken und kontinuierliche Verbesserungsmethodik institutionalisieren wir Prozesse, die zweckmäßig bleiben und adaptive Mechanismen einbetten, die strategische Initiativen skalieren lassen, ohne Compliance oder Qualität zu beeinträchtigen."
        },
        {
          icon: BarChart3,
          title: "Leistung",
          description: "Wir konstruieren evidenzbasierte Scorecards, die Wirkung in quantitativen und qualitativen Begriffen definieren. Durch Verknüpfung von Kennzahlen mit strategischen Zielen und Dateninfrastruktur statten wir die Führung mit transparenten Erkenntnissen aus, die faktenbasierte Rechenschaftspflicht, prädiktive Steuerung und disziplinierte Kommunikation von Ergebnissen an Vorstände und Stakeholder ermöglichen."
        },
        {
          icon: Zap,
          title: "Macht",
          description: "Wir architektieren Entscheidungsrechte-Frameworks, Eskalationspfade und Ermächtigungsprotokolle, die die Reaktionsfähigkeit der Führung beschleunigen. Durch Klärung von Autoritätsgrenzen und Ermöglichung verteilter Urteilsbildung erweitern wir die Fähigkeit der Organisation, unter Unsicherheit entschlossen zu handeln und dabei die Ausrichtung auf die Unternehmensstrategie aufrechtzuerhalten."
        },
        {
          icon: Handshake,
          title: "Partnerschaft",
          description: "Wir kultivieren Ökosysteme von Verbündeten, Co-Innovations-Partnern und institutionellen Sponsoren, um gemeinsame Ziele voranzutreiben. Unter Nutzung strategischer Fit-Analysen und Governance-Blueprints strukturieren wir kollaborative Vereinbarungen, die Komplementaritäten maximieren, gegenseitige Interessen schützen und gemeinsame Initiativen in nachhaltigen, gemeinsam geschaffenen Wert übersetzen."
        },
        {
          icon: Presentation,
          title: "Präsentation",
          description: "Effektive Präsentation verlangt rhetorische Klarheit und visuelle Salienz bei gleichzeitiger Aufrechterhaltung disziplinierter Untersuchung unsichtbarer Dynamiken—implizite Annahmen, affektive Unterströmungen, latente Machtasymmetrien. Durch Artikulation von Argumenten, die öffentlich resonieren, und Hinterfragung des Unausgesprochenen kultivieren Führungskräfte Glaubwürdigkeit, antizipieren Widerstand und transformieren das Publikumsverständnis jenseits des unmittelbaren Spektakels."
        }
      ]
    }
  };

  const currentContent = content[language];

  return (
    <section id="consulting-approach" className="py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            {currentContent.title}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {currentContent.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {currentContent.approaches.map((approach, index) => {
            const Icon = approach.icon;
            return (
              <div
                key={index}
                className="group p-8 rounded-2xl bg-card border border-border hover:border-accent/50 transition-smooth hover:shadow-elegant"
              >
                <div className="mb-6 p-4 rounded-lg bg-accent/10 w-fit group-hover:bg-accent/20 transition-smooth">
                  <Icon className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-2xl font-semibold mb-4">
                  {approach.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  {approach.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
